import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "./Logo";

const aboutChildren = [
  { to: "/about", label: "Overview" },
  { to: "/about/aims", label: "Aims & Values" },
  { to: "/about/team", label: "Meet the Team" },
  { to: "/news", label: "News" },
];

const links = [
  { to: "/yaz", label: "YAZ" },
  { label: "About", children: aboutChildren },
  { to: "/work", label: "Our Work" },
  { to: "/pride", label: "Pride" },
  { to: "/events", label: "Events" },
  { to: "/hire", label: "Hire" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/partners", label: "Support Us" },
  { to: "/contact", label: "Contact" },
];

function AboutDropdown() {
  const [hover, setHover] = useState(false);
  const location = useLocation();
  const active = location.pathname.startsWith("/about");
  return (
    <div className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        data-testid="nav-about"
        aria-haspopup="menu"
        aria-expanded={hover}
        onClick={() => setHover((v) => !v)}
        className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 ${active ? "text-nead-pink" : "text-nead-navy/60 hover:text-nead-pink"}`}
      >
        About
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${hover ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {hover && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-56"
          >
            <div className="rounded-2xl glass-dark border border-white/10 shadow-xl p-2">
              {aboutChildren.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  end={c.to === "/about"}
                  data-testid={`nav-about-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-white/5 text-nead-pink" : "text-white/70 hover:bg-white/5 hover:text-nead-pink"}`
                  }
                >
                  {c.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); setAboutOpen(false); }, [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-dark border-b border-white/10" data-testid="navbar">
      <nav className="container-x h-[74px] flex items-center justify-between">
        <Logo imgClassName="h-8 md:h-9" />

        <div className="hidden lg:flex items-center gap-6">
          {links.map((l) =>
            l.children ? (
              <AboutDropdown key="about" />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-300 ${isActive ? "text-nead-pink" : "text-nead-navy/60 hover:text-nead-pink"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/partners#donate"
            data-testid="nav-donate-btn"
            className="group hidden sm:inline-flex items-center gap-1.5 rounded-full bg-nead-pink px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-nead-navy"
          >
            Donate
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button className="lg:hidden p-2 text-nead-navy" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/5"
          >
            <div className="container-x py-5 flex flex-col">
              <button
                onClick={() => setAboutOpen((v) => !v)}
                data-testid="mobile-nav-about-toggle"
                className="flex items-center justify-between py-3 text-base font-medium text-nead-navy border-b border-black/5"
              >
                About
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${aboutOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {aboutOpen && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    {aboutChildren.map((c) => (
                      <Link key={c.to} to={c.to} className="block py-2.5 pl-4 text-[0.95rem] font-medium text-nead-navy/70 border-b border-black/5" data-testid={`mobile-nav-about-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {c.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              {links.filter((l) => !l.children).map((l) => (
                <Link key={l.to} to={l.to} className="py-3 text-base font-medium text-nead-navy border-b border-black/5" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/partners#donate" className="mt-4 inline-flex items-center justify-center rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white" data-testid="mobile-donate-btn">
                Donate
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
